import { useCallback, useContext, useMemo, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { styles } from '@shared/components/blocks/lib/inputQuestion';
import { MBButton } from '@shared/components/button';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { Trigger, useBoolean, useExecuteOnTrigger } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  fieldName: string;
  label: string;
  trigger: Trigger;
  disabled: boolean;
  anchorName: string;
  onChange(value: string): void;
}

export function MBCardInputBlockQuestionTextArea({
  fieldName,
  label,
  trigger,
  disabled,
  anchorName,
  onChange,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [sheetVisible, showSheet, hideSheet] = useBoolean(false);
  const { values } = useCardFormContext();
  const value = values[fieldName];
  const [text, setText] = useState(value);

  const hideSheetAndSave = useCallback(() => {
    setText(text);
    onChange(text);
    hideSheet();
  }, [onChange, text, hideSheet]);

  useExecuteOnTrigger(trigger, showSheet);

  const editForm = useMemo(() => {
    return (
      <View fill padding={2}>
        <MBAutosizeTextInput
          style={{ backgroundColor: colors.backgroundInactive, borderRadius: grid(1) }}
          onChangeText={setText}
          value={text}
          placeholder={i18n('card.blocks.question.answerPlaceholder')}
          autoFocus
          padding="normal"
        />
        <View paddingBottom={2} />
        <MBButton title={i18n('common.ok')} onPress={hideSheetAndSave} />
      </View>
    );
  }, [colors.backgroundInactive, hideSheetAndSave, text]);

  return (
    <>
      <View paddingBottom={2} style={{ anchorName }}>
        <MBTextBody1 flex>{label}</MBTextBody1>
      </View>
      {value?.length > 0 && (
        <MBTouchableOpacity disabled={disabled} onPress={showSheet}>
          <MBView style={styles.textarea} paddingVertical={2} paddingHorizontal={1}>
            <MBTextBody1>{value}</MBTextBody1>
          </MBView>
        </MBTouchableOpacity>
      )}
      {sheetVisible && (
        <AnchoredSheet
          initialSize="half"
          onClose={hideSheet}
          title={i18n('card.blocks.question.answerButtonLabel')}
          anchorName={anchorName}
          style={{ minWidth: grid(40) }}
        >
          {editForm}
        </AnchoredSheet>
      )}
    </>
  );
}
